"use client"

import HeadingElement from "@/app/(site)/_components/GCMolecules/HeadingElement"
import InformationButton from "@/app/(site)/_components/Page/Product/ProductDetail/InformationButton"
import ProductAttributeModal from "@/app/(site)/_components/Page/Product/ProductDetail/Modals/ProductAttributeModal"
import ProductAttributeTag from "@/app/(site)/_components/Page/Product/ProductDetail/ProductAttributeMapper/ProductAttributeTag"
import useEcommerceStore from "@/app/(site)/contexts/ecommerce/store"
import {
    type ProductAttribute,
    type ProductData
} from "@/lib/content-service/types/ContentServiceProduct"
import { clsx } from "@/src/utils/clsx"
import { t } from "@/translations"
import { useState } from "react"

type Attribute = "weight" | "usage_area" | "certification" | "key_benefits"

type TitleOverride = `${Attribute}_override_title`

type TitleKey = `product_page.${Attribute}`

interface AttributeConfig {
    key: Attribute
    titleOverride: TitleOverride
    titleKey: TitleKey
}

const ATTRIBUTE_CONFIG: readonly AttributeConfig[] = [
    {
        key: "weight",
        titleOverride: "weight_override_title",
        titleKey: "product_page.weight"
    },
    {
        key: "usage_area",
        titleOverride: "usage_area_override_title",
        titleKey: "product_page.usage_area"
    },
    {
        key: "certification",
        titleOverride: "certification_override_title",
        titleKey: "product_page.certification"
    },
    {
        key: "key_benefits",
        titleOverride: "key_benefits_override_title",
        titleKey: "product_page.key_benefits"
    }
]

const resolveAttributeTitle = (overrideTitle: string | undefined, fallback: string) =>
    overrideTitle && overrideTitle.trim() !== "" ? overrideTitle : fallback

interface ProductAttributeMapperProps {
    product: Pick<ProductData, Attribute | TitleOverride>
    langCode: string
}

export default function ProductAttributeMapper({
    product,
    langCode
}: ProductAttributeMapperProps) {
    const [selectedAttributes, setSelectedAttributes] = useState<ProductAttribute[]>([])
    const [selectedAttributeTitle, setSelectedAttributeTitle] = useState<string>("")
    const { toggleProductAttributeModal } = useEcommerceStore()

    return (
        <>
            <ProductAttributeModal
                attributes={selectedAttributes}
                title={selectedAttributeTitle}
            />
            {ATTRIBUTE_CONFIG.map(({ key, titleOverride, titleKey }) => {
                const attributes = product[key]
                if (!attributes || attributes.length === 0) return null

                const title = resolveAttributeTitle(
                    product[titleOverride],
                    t(langCode, titleKey)
                )
                const hasInformation = attributes.some(
                    attr => "information" in attr && attr.information
                )

                const handleAttributeClick = () => {
                    if (!hasInformation) return
                    const attributesWithInformation = attributes.filter(
                        attr => "information" in attr && attr.information
                    )
                    if (attributesWithInformation.length > 0) {
                        setSelectedAttributes(attributesWithInformation)
                        setSelectedAttributeTitle(title)
                    }
                    toggleProductAttributeModal()
                }

                return (
                    <div
                        key={key}
                        className={clsx(
                            "group flex flex-col gap-y-12",
                            hasInformation && "cursor-pointer"
                        )}
                        onClick={() => handleAttributeClick()}
                    >
                        <div className="flex h-18 items-center gap-x-4">
                            <HeadingElement
                                isAboveTheFold={false}
                                text={title}
                                type="h4"
                                styleKey="Trim_Mono_POC_Text_14_130_-5_500_uppercase"
                            />
                            {hasInformation && (
                                <InformationButton
                                    onClick={e => {
                                        e.stopPropagation()
                                        handleAttributeClick()
                                    }}
                                    ariaLabel={title}
                                    className="group-hover:text-main-black"
                                />
                            )}
                        </div>
                        <div className="flex flex-wrap gap-12">
                            {attributes.map((attr, index) => (
                                <ProductAttributeTag
                                    key={`${attr.label}-${index}`}
                                    iconUrl={attr.icon?.url}
                                    label={attr.label}
                                />
                            ))}
                        </div>
                    </div>
                )
            })}
        </>
    )
}
